import { CSSProperties, useEffect, useState } from 'react';

import { useDroppable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import produce from 'immer';
import styled from 'styled-components';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { FormError } from '@components/form-error';
import { IconButton } from '@components/icon-button';
import { IconDrag } from '@components/icons/drag';
import { IconEdit } from '@components/icons/edit';
import { IconRemove } from '@components/icons/remove';
import { IconReturn } from '@components/icons/return';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { CareerSiteLanguageType } from '@locale/locale-store';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { FLAG_MAPPER_SMALL } from 'components/iconMappers';
import { JobOption } from 'types/jobs';
import { MultiLanguageTextProps } from 'types/languages';

type DraggableOptionProps = {
  option: JobOption;
  index: number;
  languages: CareerSiteLanguageType[];
  removeOption: () => void;
  editOption: (index: number, option_display: MultiLanguageTextProps) => void;
  newId: string | null;
  isDragging?: boolean;
  displayMissing?: boolean;
};

export const DraggableOption = (props: DraggableOptionProps) => {
  const {
    option,
    index,
    languages,
    removeOption,
    editOption,
    newId,
    isDragging = false,
    displayMissing,
  } = props;
  const { t } = useScopedTranslate(OptionLocale);
  const [isNew, setIsNew] = useState<boolean>(newId === option.value);
  const [showEdit, setShowEdit] = useState<boolean>(false);
  const [langError, setLangError] = useState<CareerSiteLanguageType[]>([]);
  const [onChangeOption, setOnChangeOption] = useState(option);
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: option.value,
    data: {
      order: index,
    },
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition,
    cursor: 'grab',
    ...(isDragging && { opacity: 0.5 }),
  };

  const isSomeDisplayEmpty = languages.some((lang) => onChangeOption.display[lang] === '');
  const isEveryDisplayEmpty = languages.every((lang) => onChangeOption.display[lang] === '');
  const isTaskDirty = onChangeOption.display !== option.display;

  const multiLanguage = languages.length > 1;

  const save = () => {
    const newLangError = [...langError];
    if (!isSomeDisplayEmpty) {
      editOption(index, onChangeOption.display);
      if (isNew) {
        setIsNew(false);
      } else {
        setShowEdit(false);
      }
    } else {
      languages.forEach((lang) => {
        if (!langError.includes(lang) && onChangeOption.display[lang] === '') {
          newLangError.push(lang);
        }
      });
    }
    setLangError(newLangError);
  };

  useEffect(() => {
    langError.forEach((lang) => {
      if (onChangeOption.display[lang] !== '') {
        setLangError(langError.filter((l) => l !== lang));
      }
    });
  });

  const onCancel = () => {
    if (isNew) {
      removeOption();
    } else {
      setOnChangeOption(option);
      setShowEdit(false);
    }
  };

  return showEdit || isNew ? (
    <StyledCard variant="gray" space={2}>
      <Stack space={2} orientation={'vertical'}>
        <Stack style={{ width: '100%' }} space={2}>
          {languages.map((language) => (
            <TextField
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  save();
                }
              }}
              onBlur={() => {
                if (multiLanguage && isSomeDisplayEmpty && !isEveryDisplayEmpty) {
                  setLangError([...langError, language]);
                }
              }}
              autoFocus={
                !multiLanguage ||
                (multiLanguage && onChangeOption.display[languages[0]] !== '') ||
                (multiLanguage && languages[0] === language)
              }
              key={language}
              id={language}
              placeholder={t('addOption')}
              label={t('add')}
              hideLabel
              type="text"
              value={onChangeOption ? onChangeOption.display[language] : ''}
              onChange={(value) =>
                setOnChangeOption(
                  produce((draft) => {
                    draft.display[language] = value;
                  })
                )
              }
              iconLeft={multiLanguage ? FLAG_MAPPER_SMALL[language] : undefined}
              error={
                (langError && langError.includes(language)) ||
                (!isNew && onChangeOption.display[language] === '')
                  ? multiLanguage
                    ? t('langError')
                    : t('optionMissing')
                  : undefined
              }
            />
          ))}
        </Stack>
        <Stack space={2} orientation="horizontal" justify="end">
          <Button onClick={onCancel} text={t('cancel')} variant="secondary" />
          <Button
            isDisabled={isSomeDisplayEmpty || !isTaskDirty}
            onClick={() => save()}
            variant="primary-blue"
            text={isNew ? t('add') : t('update')}
            iconLeft={<IconReturn />}
          />
        </Stack>
      </Stack>
    </StyledCard>
  ) : (
    <div ref={setNodeRef} style={style}>
      <Card
        key={`option-${index}`}
        variant="transparent"
        interactive
        space={1}
        {...attributes}
        {...listeners}
      >
        <Stack space={4} orientation="horizontal" align="center" justify="space-between">
          <Stack orientation="horizontal" align="center" space={1}>
            <StyledDragIcon align="center" justify="center">
              <IconDrag color="purple50" />
            </StyledDragIcon>
            <Stack space={1}>
              {languages.map((language, index) => (
                <Stack orientation="horizontal" space={1} key={index} align="center">
                  {multiLanguage && FLAG_MAPPER_SMALL[language]}
                  {Boolean(option.display[language]) ? (
                    <Text
                      key={`option-${index}-${language}`}
                      text={option.display[language]}
                      font={multiLanguage ? 'regular14' : 'regular16'}
                      style={{ wordBreak: 'break-word' }}
                    />
                  ) : (
                    <Text text={t('noTranslation')} font="regular14" color="purple50" />
                  )}
                </Stack>
              ))}
              {displayMissing && <FormError text={t('optionMissing')} />}
            </Stack>
          </Stack>
          <Stack orientation="horizontal">
            <IconButton
              icon={<IconEdit />}
              onClick={() => setShowEdit(true)}
              variant="ghost-purple"
            />
            <IconButton icon={<IconRemove />} onClick={removeOption} variant="ghost-purple" />
          </Stack>
        </Stack>
      </Card>
    </div>
  );
};

const StyledCard = styled(Card)({
  border: `${pxToRem(2)} solid`,
  borderColor: `${theme.color.blue100} !important`,
});

const StyledDragIcon = styled(Stack)({
  width: pxToRem(40),
  minWidth: pxToRem(40),
});

type QuestionCardProps = {
  option: JobOption;
  index: number;
  languages: CareerSiteLanguageType[];
};

export const OptionCard = (props: QuestionCardProps) => {
  const { option, index, languages } = props;
  const { t } = useScopedTranslate(OptionLocale);
  const { setNodeRef } = useDroppable({
    id: option.value,
    data: {
      order: index,
    },
  });

  const multiLanguage = languages.length > 1;

  return (
    <div ref={setNodeRef}>
      <Card key={`option-${index}`} style={{ background: theme.color.purple5 }} space={2}>
        <Stack space={4} orientation="horizontal" align="center" justify="space-between">
          <Stack orientation="horizontal" align="center" space={1}>
            <StyledDragIcon align="center" justify="center">
              <IconDrag color="purple50" />
            </StyledDragIcon>
            <Stack space={1}>
              {languages.map((language, index) => (
                <Stack orientation="horizontal" space={1} key={index} align="center">
                  {multiLanguage && FLAG_MAPPER_SMALL[language]}
                  {Boolean(option.display[language]) ? (
                    <Text
                      key={`option-${index}-${language}`}
                      text={option.display[language]}
                      font={multiLanguage ? 'regular14' : 'regular16'}
                      style={{ wordBreak: 'break-word' }}
                    />
                  ) : (
                    <Text text={t('noTranslation')} font="regular14" color="purple50" />
                  )}
                </Stack>
              ))}
            </Stack>
          </Stack>
          <Stack orientation="horizontal">
            <IconButton icon={<IconEdit />} variant="ghost-purple" />
            <IconButton icon={<IconRemove />} variant="ghost-purple" />
          </Stack>
        </Stack>
      </Card>
    </div>
  );
};

const OptionLocale = {
  en: {
    addOption: 'Add option',
    add: 'Add',
    update: 'Update',
    cancel: 'Cancel',
    langError: 'Translation missing',
    optionMissing: 'Option missing',
    noTranslation: 'No translation',
  },
  is: {
    addOption: 'Bæta við valmöguleika',
    add: 'Bæta við',
    update: 'Uppfæra',
    cancel: 'Hætta við',
    langError: 'Þýðingu vantar',
    optionMissing: 'Valmöguleika vantar',
    noTranslation: 'Engin þýðing',
  },
};
